<script setup>
import { computed, ref } from 'vue';
import { ANALYTICS_PRESET } from '@pfsc/core/services/analytics.service';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  periodLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const PRESETS = [
  { value: ANALYTICS_PRESET.TODAY, label: 'Today' },
  { value: ANALYTICS_PRESET.YESTERDAY, label: 'Yesterday' },
  { value: ANALYTICS_PRESET.LAST_7_DAYS, label: '7 days' },
  { value: ANALYTICS_PRESET.LAST_30_DAYS, label: '30 days' },
  { value: ANALYTICS_PRESET.LAST_90_DAYS, label: '90 days' },
  { value: ANALYTICS_PRESET.MONTH_TO_DATE, label: 'This month' },
  { value: ANALYTICS_PRESET.YEAR_TO_DATE, label: 'This year' },
  { value: ANALYTICS_PRESET.LAST_12_MONTHS, label: '12 months' },
];

const isCustomOpen = ref(false);
const draftStart = ref(props.modelValue.startDate ?? '');
const draftEnd = ref(props.modelValue.endDate ?? '');

const isCustom = computed(() => props.modelValue.preset === ANALYTICS_PRESET.CUSTOM);

/**
 * Select a named window.
 * @param {string} preset Preset key.
 */
function choosePreset(preset) {
  isCustomOpen.value = false;
  emit('update:modelValue', { ...props.modelValue, preset, startDate: null, endDate: null });
}

/** Open the custom range editor, seeded from the window on screen. */
function openCustom() {
  draftStart.value = props.modelValue.startDate ?? '';
  draftEnd.value = props.modelValue.endDate ?? '';
  isCustomOpen.value = !isCustomOpen.value;
}

const isDraftValid = computed(() => Boolean(draftStart.value)
  && Boolean(draftEnd.value)
  && draftStart.value <= draftEnd.value);

/** Apply the custom range. */
function applyCustom() {
  if (!isDraftValid.value) {
    return;
  }
  isCustomOpen.value = false;
  emit('update:modelValue', {
    ...props.modelValue,
    preset: ANALYTICS_PRESET.CUSTOM,
    startDate: draftStart.value,
    endDate: draftEnd.value,
  });
}
</script>

<template>
  <div :class="$style.wrapper">
    <div :class="$style.chips">
      <button
        v-for="preset in PRESETS"
        :key="preset.value"
        type="button"
        :class="[$style.chip, modelValue.preset === preset.value ? $style.chipActive : '']"
        @click="choosePreset(preset.value)"
      >
        {{ preset.label }}
      </button>
      <button
        type="button"
        :class="[$style.chip, isCustom ? $style.chipActive : '']"
        @click="openCustom"
      >
        Custom
      </button>
    </div>

    <div v-if="isCustomOpen" :class="$style.customPanel">
      <label :class="$style.field">
        <span :class="$style.fieldLabel">From</span>
        <input v-model="draftStart" type="date" :class="$style.input">
      </label>
      <label :class="$style.field">
        <span :class="$style.fieldLabel">To</span>
        <input v-model="draftEnd" type="date" :class="$style.input">
      </label>
      <button
        type="button"
        class="primary"
        :class="$style.apply"
        :disabled="!isDraftValid"
        @click="applyCustom"
      >
        Apply
      </button>
      <p :class="$style.note">
        Dates are read in Pacific time, the same clock the reports are keyed to.
      </p>
    </div>

    <p v-if="periodLabel" :class="$style.periodLabel">
      {{ periodLabel }}
    </p>
  </div>
</template>

<style module>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.chip {
  border: 1px solid var(--border-color);
  background: white;
  color: var(--primary-font-color);
  border-radius: var(--border-radius-xxl);
  padding: 7px 16px;
  font-size: var(--font-size-lg);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.chip:hover {
  border-color: var(--pfs-red);
}
.chipActive {
  background: var(--pfs-red);
  border-color: var(--pfs-red);
  color: white;
}
.customPanel {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 14px;
  background: white;
  border: 1px solid var(--border-color-light);
  border-radius: var(--border-radius-lg);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.fieldLabel {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.input {
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 7px 10px;
  font-family: inherit;
  font-size: var(--font-size-lg);
}
.apply {
  padding: 8px 18px;
}
.note {
  flex-basis: 100%;
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.periodLabel {
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--faint-color);
}
</style>
